/**
 * PostHog analytics wrapper for MachinePilot.
 * - EU region (https://eu.i.posthog.com)
 * - Loaded lazily and ONLY if the user has granted analytics consent (GDPR)
 * - Auto-capture + page views + custom events (track, identify, reset)
 */
import posthog from "posthog-js";

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || "https://eu.i.posthog.com";
const CONSENT_KEY = "mp_cookie_consent";

let booted = false;
let identifiedId = null;
// Super properties (attached to every event, autocapture included). Kept in a
// module variable so values set before PostHog boots are not lost.
const superProps = {};

function applySuperProps() {
  if (!booted || !Object.keys(superProps).length) return;
  try { posthog.register(superProps); } catch (e) { devLog("register", e); }
}

/** UI language of the current session (it/en/de/fr/es/pl). */
export function setAnalyticsLanguage(language) {
  if (!language) return;
  superProps.language = language;
  applySuperProps();
}

/** Country of the signed-in workshop (ISO alpha-2, ZZ = other). */
export function setAnalyticsCountry(country) {
  superProps.country = country || null;
  applySuperProps();
}

/** Admin accounts, /admin pages and Emergent workspace traffic. */
export function markInternalUser(reason = "internal") {
  if (superProps.internal_user) return;
  superProps.internal_user = true;
  superProps.internal_reason = reason;
  applySuperProps();
}

// Dev-only logger for the otherwise silent analytics failures. Kept out of
// production builds so legitimate ad-blocker / consent-denied / offline
// scenarios don't spam the console for end users.
const devLog = (where, err) => {
  if (process.env.NODE_ENV === "development") {
    console.warn(`[analytics:${where}]`, err);
  }
};

export function isAnalyticsAllowed() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;
    const p = JSON.parse(raw);
    return !!p?.analytics;
  } catch {
    return false;
  }
}

export function initPostHog() {
  if (booted || !KEY || !isAnalyticsAllowed()) return;
  posthog.init(KEY, {
    api_host: HOST,
    person_profiles: "identified_only",
    capture_pageview: false,        // we'll fire pageviews on route change
    capture_pageleave: true,
    autocapture: true,
    disable_session_recording: true,
    loaded: (ph) => {
      // Honour DNT in EU
      if (window.navigator?.doNotTrack === "1") ph.opt_out_capturing();
    },
  });
  booted = true;
  applySuperProps();
}

export function shutdownPostHog() {
  identifiedId = null;
  if (!booted) return;
  try { posthog.opt_out_capturing(); } catch (e) { devLog("opt_out", e); }
  try { posthog.reset(); } catch (e) { devLog("reset", e); }
  booted = false;
}

// QA hook: add ?mp_debug_analytics=1 to any URL to print every event to the
// browser console (PostHog itself drops events sent from headless browsers).
const analyticsDebug = () => {
  try {
    if (/[?&]mp_debug_analytics=1/.test(window.location.search)) {
      localStorage.setItem("mp_debug_analytics", "1");
      return true;
    }
    return localStorage.getItem("mp_debug_analytics") === "1";
  } catch {
    return false;
  }
};

function debugEvent(event, props) {
  if (analyticsDebug()) console.log("[mp-analytics]", event, { ...superProps, ...props });
}

export function track(event, props = {}) {
  debugEvent(event, props);
  if (!booted) return;
  try { posthog.capture(event, props); } catch (e) { devLog("capture", e); }
}

export function identifyUser(user) {
  if (!user?.id) return;
  if (user.country) setAnalyticsCountry(user.country);   // keep super prop fresh
  if (identifiedId === user.id) return;
  identifiedId = user.id;
  debugEvent("$identify", { distinct_id: user.id, country: user.country });
  if (!booted) return;
  try {
    posthog.identify(user.id, {
      email: user.email,
      company_name: user.company_name,
      tier: user.tier,
      country: user.country,
      role: user.role,
    });
  } catch (e) { devLog("identify", e); }
}

export function resetIdentity() {
  identifiedId = null;
  if (!booted) return;
  try { posthog.reset(); } catch (e) { devLog("reset", e); }
}

export function capturePageview(path) {
  debugEvent("$pageview", { path });
  if (!booted) return;
  try { posthog.capture("$pageview", { $current_url: window.location.href, path }); } catch (e) { devLog("pageview", e); }
}

// Context sent with server-side events (backend `quote_generated`): the
// backend only emits when the browser holds analytics consent.
export function getAnalyticsContext() {
  let consent = isAnalyticsAllowed() && !!KEY;
  let sessionId = null;
  if (consent && booted) {
    try {
      if (posthog.has_opted_out_capturing()) consent = false;
      else sessionId = posthog.get_session_id() || null;
    } catch (e) { devLog("session", e); }
  }
  return {
    analytics_consent: consent,
    analytics_session_id: sessionId,
    analytics_internal_reason: superProps.internal_user ? superProps.internal_reason : null,
    analytics_country: superProps.country || null,
  };
}

/** QA visibility for events emitted by the backend (debug flag only). */
export function debugServerEvent(event, props) {
  debugEvent(`${event} (server-side)`, props);
}

// Re-check consent and (re)init/shutdown accordingly.
export function refreshConsent() {
  if (isAnalyticsAllowed()) initPostHog();
  else shutdownPostHog();
}

export default { initPostHog, shutdownPostHog, track, identifyUser, capturePageview, refreshConsent, isAnalyticsAllowed, setAnalyticsLanguage, setAnalyticsCountry, markInternalUser };
